import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import InfoButton from './InfoButton';
import { fieldNodesInfo } from './operationsInfo';
import {
  getFieldNodes,
  createFieldNode,
  sellFieldNode,
  updateFieldNode,
} from '../../services/adminService';
import { getRegisteredFields } from '../../services/fieldService';
import {
  FieldNode,
  FieldNodePayload,
  FieldNodeUpdatePayload,
} from '../../types/device';
import { Field } from '../../types/field';
import {
  AdminTable,
  AdminTableRow,
  AdminPrimaryButton,
  AdminSecondaryButton,
  AdminInlineButton,
  AdminForm,
  AdminFormGroup,
  AdminFormLabel,
  AdminFormInput,
  AdminFormSelect,
  AdminErrorMessage,
  AdminSuccessMessage,
  AdminLoadingMessage,
  AdminFormActions,
  AdminEmptyState,
  adminColors,
} from '../../styles/adminStyles';

const Container = styled.div`
  margin-bottom: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.75rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0;
  font-weight: 700;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 80px 140px 100px 100px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 164, 255, 0.05) 100%);
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffffff !important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 14px 20px;
  border-bottom: 1px solid ${adminColors.borderLight};
`;

const TableRow = styled(AdminTableRow)`
  grid-template-columns: 100px 1fr 80px 140px 100px 100px;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 13px;

  & > div {
    color: #ffffff !important;
    font-size: 13px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ffffff !important;
  font-weight: 700;
`;

const NodeTypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ $type }) => {
    switch ($type) {
      case 'OfficeNode': return 'rgba(255, 165, 0, 0.2)';
      case 'MapNode': return 'rgba(0, 255, 136, 0.2)';
      default: return 'rgba(128, 128, 128, 0.2)';
    }
  }};
  color: #ffffff !important;
  border: 1px solid ${({ $type }) => {
    switch ($type) {
      case 'OfficeNode': return 'rgba(255, 165, 0, 0.4)';
      case 'MapNode': return 'rgba(0, 255, 136, 0.4)';
      default: return 'rgba(128, 128, 128, 0.4)';
    }
  }};
`;

const SellSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: rgba(255, 165, 0, 0.1);
  border: 1px solid rgba(255, 165, 0, 0.3);
  border-radius: 8px;
`;

const SellTitle = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ffffff !important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const WhiteEmptyState = styled(AdminEmptyState)`
  color: #ffffff !important;
  font-weight: 700;
  font-size: 14px;
`;

const DataTable = styled.div`
  border: 1px solid rgba(0, 212, 255, 0.3);
  background: rgba(0, 30, 60, 0.3);
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
`;

interface FormState {
  saleNumber: string;
  nodeCode: string;
  nodeType: string;
}

const initialFormState: FormState = {
  saleNumber: '',
  nodeCode: '',
  nodeType: 'MapNode',
};

const NODE_TYPES = [
  { value: 'OfficeNode', label: 'Office Node' },
  { value: 'MapNode', label: 'Map Node' },
];

const FieldNodeManager: React.FC = () => {
  const [nodes, setNodes] = useState<FieldNode[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [sellModalOpen, setSellModalOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [editFormState, setEditFormState] = useState<FormState>(initialFormState);
  const [sellFieldId, setSellFieldId] = useState('');
  const [selectedNode, setSelectedNode] = useState<FieldNode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState<Field[]>([]);

  const pageSize = 20;

  const loadFields = async () => {
    try {
      const data = await getRegisteredFields();
      setFields(data || []);
    } catch (err) {
      console.error('Failed to load fields', err);
    }
  };

  const loadNodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFieldNodes(page, pageSize);
      setNodes(data.records || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Failed to load nodes', err);
      setError(err instanceof Error ? err.message : 'Unable to load nodes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFields();
  }, []);

  useEffect(() => {
    loadNodes();
  }, [page]);

  const getFieldName = (fieldId: string | undefined) => {
    if (!fieldId) return '-';
    const field = fields.find(f => f.id === fieldId);
    return field ? `${field.name} (${field.state})` : fieldId;
  };

  const handleCreateClick = () => {
    setFormState(initialFormState);
    setError(null);
    setSuccess(null);
    setModalOpen(true);
  };

  const handleEditClick = (node: FieldNode) => {
    setSelectedNode(node);
    setEditFormState({
      saleNumber: node.saleNumber || '',
      nodeCode: node.nodeCode || '',
      nodeType: node.nodeType || 'MapNode',
    });
    setError(null);
    setSuccess(null);
    setEditModalOpen(true);
  };

  const handleSellClick = (node: FieldNode) => {
    setSelectedNode(node);
    setSellFieldId('');
    setError(null);
    setSuccess(null);
    setSellModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formState.nodeCode.trim() || !formState.nodeType) {
      setError('Node Code and Node Type are required');
      return;
    }

    const payload: FieldNodePayload = {
      saleNumber: formState.saleNumber.trim() || undefined,
      nodeCode: formState.nodeCode.trim(),
      nodeType: formState.nodeType,
    };

    setSaving(true);
    try {
      await createFieldNode(payload);
      setSuccess('Node created successfully');
      setModalOpen(false);
      setFormState(initialFormState);
      await loadNodes();
    } catch (err) {
      console.error('Failed to create node', err);
      setError(err instanceof Error ? err.message : 'Unable to create node');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!editFormState.nodeCode.trim() || !editFormState.nodeType) {
      setError('Node Code and Node Type are required');
      return;
    }

    if (!selectedNode) {
      setError('No node selected');
      return;
    }

    const payload: FieldNodeUpdatePayload = {
      nodeCode: editFormState.nodeCode.trim(),
      nodeType: editFormState.nodeType,
    };

    setSaving(true);
    try {
      await updateFieldNode(selectedNode.id, payload);
      setSuccess('Node updated successfully');
      setEditModalOpen(false);
      setSelectedNode(null);
      await loadNodes();
    } catch (err) {
      console.error('Failed to update node', err);
      setError(err instanceof Error ? err.message : 'Unable to update node');
    } finally {
      setSaving(false);
    }
  };

  const handleSellSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!sellFieldId.trim()) {
      setError('Field ID is required');
      return;
    }

    if (!selectedNode) {
      setError('No node selected');
      return;
    }

    setSaving(true);
    try {
      await sellFieldNode(selectedNode.id, { fieldId: sellFieldId.trim() });
      setSuccess('Node sold successfully');
      setSellModalOpen(false);
      setSellFieldId('');
      setSelectedNode(null);
      await loadNodes();
    } catch (err) {
      console.error('Failed to sell node', err);
      setError(err instanceof Error ? err.message : 'Unable to sell node');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Container>
      <PageHeader>
        <PageTitle>Field Nodes</PageTitle>
        <InfoButton title="Field Nodes Help" sections={fieldNodesInfo} />
      </PageHeader>
      <HeaderActions>
        <div></div>
        <AdminPrimaryButton onClick={handleCreateClick}>+ Create Node</AdminPrimaryButton>
      </HeaderActions>

      {loading && <AdminLoadingMessage>Loading...</AdminLoadingMessage>}
      {error && !modalOpen && !editModalOpen && !sellModalOpen && <AdminErrorMessage>{error}</AdminErrorMessage>}

      <DataTable>
        <TableHeader>
          <div>Node Code</div>
          <div>Sale #</div>
          <div>Type</div>
          <div>Field</div>
          <div>Sold Date</div>
          <div>Actions</div>
        </TableHeader>
        {nodes.map((node) => (
          <TableRow key={node.id}>
            <div style={{ fontFamily: 'Courier New', fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>
              {node.nodeCode || '-'}
            </div>
            <div style={{ fontFamily: 'Courier New', fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>
              {node.saleNumber || '-'}
            </div>
            <div>
              <NodeTypeBadge $type={node.nodeType}>
                {node.nodeType.replace('Node', '')}
              </NodeTypeBadge>
            </div>
            <div style={{ fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>
              {getFieldName(node.fieldId)}
            </div>
            <div style={{ fontFamily: 'Courier New', fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>
              {node.soldAt ? new Date(node.soldAt).toLocaleDateString() : '-'}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <AdminInlineButton onClick={() => handleEditClick(node)}>Edit</AdminInlineButton>
              {!node.soldAt && (
                <AdminInlineButton onClick={() => handleSellClick(node)}>Sell</AdminInlineButton>
              )}
            </div>
          </TableRow>
        ))}
        {nodes.length === 0 && !loading && (
          <WhiteEmptyState>No nodes found</WhiteEmptyState>
        )}
      </DataTable>

      {totalPages > 1 && (
        <Pagination>
          <AdminSecondaryButton disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            Prev
          </AdminSecondaryButton>
          <span>Page {page} of {totalPages}</span>
          <AdminSecondaryButton disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            Next
          </AdminSecondaryButton>
        </Pagination>
      )}

      {/* Create Node Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setFormState(initialFormState);
        }}
        title="Create Field Node"
      >
        <AdminForm onSubmit={handleSubmit}>
          <FormRow>
            <AdminFormGroup>
              <AdminFormLabel>Node Code *</AdminFormLabel>
              <AdminFormInput
                value={formState.nodeCode}
                onChange={(e) => setFormState({ ...formState, nodeCode: e.target.value })}
                placeholder="Unique code for map binding"
              />
            </AdminFormGroup>
            <AdminFormGroup>
              <AdminFormLabel>Node Type *</AdminFormLabel>
              <AdminFormSelect
                value={formState.nodeType}
                onChange={(e) => setFormState({ ...formState, nodeType: e.target.value })}
              >
                {NODE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </AdminFormSelect>
            </AdminFormGroup>
          </FormRow>

          <AdminFormGroup>
            <AdminFormLabel>Sale Number (optional)</AdminFormLabel>
            <AdminFormInput
              value={formState.saleNumber}
              onChange={(e) => setFormState({ ...formState, saleNumber: e.target.value })}
              placeholder="Leave empty to auto-generate"
            />
          </AdminFormGroup>

          {error && <AdminErrorMessage>{error}</AdminErrorMessage>}
          {success && <AdminSuccessMessage>{success}</AdminSuccessMessage>}

          <AdminFormActions>
            <AdminSecondaryButton type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </AdminSecondaryButton>
            <AdminPrimaryButton type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Node'}
            </AdminPrimaryButton>
          </AdminFormActions>
        </AdminForm>
      </Modal>

      {/* Edit Node Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedNode(null);
        }}
        title="Edit Field Node"
      >
        <AdminForm onSubmit={handleEditSubmit}>
          {selectedNode && (
            <SellSection style={{ background: 'rgba(0, 212, 255, 0.1)', borderColor: 'rgba(0, 212, 255, 0.3)', marginBottom: '16px' }}>
              <SellTitle>Sale Number (auto-generated)</SellTitle>
              <div style={{ fontFamily: 'Courier New', fontSize: '12px', color: '#fff' }}>
                {selectedNode.saleNumber || '-'}
              </div>
            </SellSection>
          )}

          <FormRow>
            <AdminFormGroup>
              <AdminFormLabel>Node Code *</AdminFormLabel>
              <AdminFormInput
                value={editFormState.nodeCode}
                onChange={(e) => setEditFormState({ ...editFormState, nodeCode: e.target.value })}
                placeholder="Unique code for map binding"
              />
            </AdminFormGroup>
            <AdminFormGroup>
              <AdminFormLabel>Node Type *</AdminFormLabel>
              <AdminFormSelect
                value={editFormState.nodeType}
                onChange={(e) => setEditFormState({ ...editFormState, nodeType: e.target.value })}
              >
                {NODE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </AdminFormSelect>
            </AdminFormGroup>
          </FormRow>

          {error && <AdminErrorMessage>{error}</AdminErrorMessage>}
          {success && <AdminSuccessMessage>{success}</AdminSuccessMessage>}

          <AdminFormActions>
            <AdminSecondaryButton type="button" onClick={() => setEditModalOpen(false)}>
              Cancel
            </AdminSecondaryButton>
            <AdminPrimaryButton type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </AdminPrimaryButton>
          </AdminFormActions>
        </AdminForm>
      </Modal>

      {/* Sell Node Modal */}
      <Modal
        isOpen={sellModalOpen}
        onClose={() => {
          setSellModalOpen(false);
          setSellFieldId('');
          setSelectedNode(null);
        }}
        title="Sell Node to Field"
      >
        <AdminForm onSubmit={handleSellSubmit}>
          {selectedNode && (
            <SellSection>
              <SellTitle>Selected Node</SellTitle>
              <div style={{ fontFamily: 'Courier New', fontSize: '12px', color: '#fff' }}>
                <div><strong>Node Code:</strong> {selectedNode.nodeCode}</div>
                <div><strong>Type:</strong> {selectedNode.nodeType}</div>
              </div>
            </SellSection>
          )}

          <AdminFormGroup style={{ marginTop: '16px' }}>
            <AdminFormLabel>Select Field *</AdminFormLabel>
            <AdminFormSelect
              value={sellFieldId}
              onChange={(e) => setSellFieldId(e.target.value)}
            >
              <option value="">-- Select Field --</option>
              {fields.map(field => (
                <option key={field.id} value={field.id}>
                  {field.name}
                </option>
              ))}
            </AdminFormSelect>
          </AdminFormGroup>

          {error && <AdminErrorMessage>{error}</AdminErrorMessage>}
          {success && <AdminSuccessMessage>{success}</AdminSuccessMessage>}

          <AdminFormActions>
            <AdminSecondaryButton type="button" onClick={() => setSellModalOpen(false)}>
              Cancel
            </AdminSecondaryButton>
            <AdminPrimaryButton type="submit" disabled={saving}>
              {saving ? 'Selling...' : 'Sell Node'}
            </AdminPrimaryButton>
          </AdminFormActions>
        </AdminForm>
      </Modal>
    </Container>
  );
};

export default FieldNodeManager;
